import React, {
  useState,
  useEffect
} from 'react';

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link as RouterLink,
} from "react-router-dom";

import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Breadcrumbs,
  Link,
  Grid,
  Typography,
  Button,
  IconButton,
  Divider,
  Collapse,
  ListSubheader,
} from '@material-ui/core';
import {
  Skeleton
} from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import EditIcon from '@material-ui/icons/Edit';

import {
  IssueReportPanel,
  ThesisProposalDetail,
  StatusTimeline,
  ThesisProposalForm,
  CurrentStatus,
  DateTimeView,
  FileCommentsHistory,
  AdminNotes,
} from '../views';

import { useStatus } from '../data/Status';
import filterDocumentBy from '../utils/filterDocumentBy';
import grabAllDocuments from '../utils/grabAllDocuments';
import {ThesisProposalGradingReviewViewer} from '../views/thesis';
import {
  Document,
  ReviewAssignment,
} from '../types';
import localization from '../utils/localizations';

const appConfig = (window as any).APP_CONFIG;


const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  secondPanel: {
    padding: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  pageTitle: {
    marginTop: 10,
    marginBottom: 10,
  },
  breadcrumbs: {
    margin: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  gridLabel: {
    textAlign: 'right',
    color: '#555555',
  },
  alignRight: {
    textAlign: 'right',
  },
  ugbOpenMessage: {
    marginTop: 20,
    marginBottom: 20,
  }
}));


function ThesisProposalPage() {
  const classes = useStyles();

  const [status, statusLoading, loadStatus] = useStatus();
  const [showForm, setShowForm] = useState(false);
  let allDocuments: Document[] = [];

  let currentData = undefined;
  let editButtonLabel = `${localization.submit} ${localization.proposal}`;
  if (status?.proposal?.id) {
    currentData = {
      title: status.proposal.title,
      primary_advisor: status.proposal.primary_advisor?.id,
      additional_advisors: status.proposal.additional_advisors.map((advisor: {id: string}) => advisor.id),
      advisor_research: status.proposal.advisor_research,
      proposal_docx: filterDocumentBy(status.proposal.documents, {kind: 'Proposal', content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}),
      proposal_pdf: filterDocumentBy(status.proposal.documents, {kind: 'Proposal', content_type: "application/pdf"}),
      ksm: filterDocumentBy(status.proposal.documents, {kind: 'KSM'}),
    }
    if ((status.proposal.status == 'approved') || (status.proposal.status == 'submitted') || (status.proposal.status == 'approved_with_revision') || (status.proposal.status == 'presentation_required') || (status.proposal.status == 'closed')) {
      editButtonLabel = localization.updateData;
    }
    allDocuments = grabAllDocuments(status.proposal.documents, status.proposal.status_history);
  }

  let disableEdit = !(status?.proposal);
  let submissionDeadlineReached = false;
  let deadline = status?.current_ugb_period?.proposal_end;
  if (deadline) {
    if (moment() > moment(deadline)) {
      disableEdit = true;
      submissionDeadlineReached = true;
    }
  }

  if (!deadline) {
    disableEdit = true;
  }

  if (status && status.proposal && status.proposal.id) {
    if ((status.proposal.status == 'approved') || (status.proposal.status == 'closed')) {
      disableEdit = true;
    }
  }

  return (
    <>
    <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
      <Link color="inherit" to={`${appConfig.homepage}`} component={RouterLink}>
        {localization.home}
      </Link>
      <Typography color="textPrimary">{localization.proposal}</Typography>
    </Breadcrumbs>

    <Paper className={classes.mainBody}>
      <ListSubheader style={{paddingLeft: 8}}>{localization.proposalPageTitle}</ListSubheader>

      <Collapse
        in={!showForm}
      >
        {!status.success ? (
          <div style={{position: 'relative', padding: 10}}>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 10}}/>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 40}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
          </div>
        ) : (
          submissionDeadlineReached ?
          (<Alert
            severity="error"
            className={classes.ugbOpenMessage}
          >
            {localization.submissionHasBeenClosedOn} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> (<DateTimeView value={deadline} fromNow />)
          </Alert>)
          :
          (!!deadline ? <Alert
            severity="info"
            className={classes.ugbOpenMessage}
          >
            {localization.proposalSubmissionOpenUntil} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> (<DateTimeView value={deadline} fromNow />)
          </Alert> :
          <Alert
            severity="info"
            className={classes.ugbOpenMessage}
          >
            {localization.proposalSubmissionNotOpen}
          </Alert>
          )
        )}

        {status && status.proposal && status.proposal.status &&
          <CurrentStatus
            currentStatus={status.proposal.status}
            history={status.proposal.status_history}
            namespace="proposal"
            statusMap={[
              {
                status: 'draft',
                label: localization.submission,
                description: localization.awaitingSubmission,
                descriptionCompleted: localization.submissionSent,
              },
              {
                status: 'submitted',
                label: localization.review,
                description: localization.awaitingApproval,
                descriptionCompleted: localization.approved,
              },
              {
                status: 'scheduled',
                label: localization.scheduled,
                description: localization.presentationScheduled,
              },
              {
                status: 'approved',
                label: localization.approved,
                description: localization.proposalAccepted,
              },
            ]}
          />
        }

        {status && status.proposal && status.proposal.status &&
          <ThesisProposalDetail proposal={status.proposal} />
        }

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon/>}
              onClick={(e: any) => {
                setShowForm(true);
              }}
              disabled={disableEdit}
            >{editButtonLabel}</Button>
          </Grid>
          <Grid item xs={6} className={classes.alignRight}>
            <Button
              variant="contained"
              to={`${appConfig.homepage}`} component={RouterLink}
            >{localization.backToHome}</Button>
          </Grid>
        </Grid>
      </Collapse>

      <Collapse
        in={showForm}
        {...(showForm ? { timeout: 1000 } : {})}
      >
        <ThesisProposalForm
          onSubmitted={() => {
            setShowForm(false);
            loadStatus();
          }}
          onCancelled={() => {
            setShowForm(false);
          }}
          values={currentData}
        />
      </Collapse>
    </Paper>

    {(status?.proposal?.review_assignments) &&	status?.proposal?.status !== 'submitted' && 
      <div>
        {(status.proposal.review_assignments as ReviewAssignment[]).map((assignment, i) => {
          if (assignment.proposal_review) {
            return (
              <ThesisProposalGradingReviewViewer
                key={`review-${assignment.proposal_review}`}
                reviewId={assignment.proposal_review}
                reviewerName={`Reviewer ${i+1}`}
              />
            );
          }
          else return <></>
        })}
      </div>}

    {(status?.proposal?.notes?.length > 0) &&
      <AdminNotes notes={status.proposal.notes} />
    }

    {status && status.proposal && (allDocuments.length > 0) && <FileCommentsHistory documents={allDocuments} collapsed={true} hideIfEmpty={true} /> }

    {status && status.ugb && <StatusTimeline history={status.proposal.status_history} collapsed={true} hideIfEmpty={true}/> }

    <IssueReportPanel />
    </>
  );
}

export default ThesisProposalPage;